'use client';

import React from 'react';
import { 
    Camera, 
    Upload, 
    Search, 
    RotateCw, 
    BookCopy, 
    Building2, 
    User, 
    Loader2 
} from 'lucide-react';
import { 
    DropdownMenu, 
    DropdownMenuTrigger, 
    DropdownMenuContent, 
    DropdownMenuItem 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { AgencyProfile, S3File } from '@/doi-tac-module/lib/types';
import { AgencyImageField } from '@/doi-tac-module/hooks/use-agency-image-upload';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { cn } from '@/doi-tac-module/lib/utils';

interface AgencySharedImageFieldProps {
    variant: 'box' | 'avatar';
    field: AgencyImageField; 
    profileData: Partial<AgencyProfile>; 
    
    isImageSubmitting: boolean;
    uploadProgress: number | undefined; 
    
    getImageUrl: (profile: Partial<AgencyProfile>, field: AgencyImageField) => string | null | undefined;
    getOriginalImageUrl: (profile: Partial<AgencyProfile>, field: AgencyImageField) => string | null | undefined;
    onFileSelected: (e: React.ChangeEvent<HTMLInputElement>, field: AgencyImageField) => void;
    handleRecrop: (imageUrl: string, field: AgencyImageField) => void;
    handleOpenHistory: (field: AgencyImageField) => void;
    openLightbox: (images: string[]) => void;
    
    language: 'vi' | 'bn';
    disabled?: boolean;
    readOnly?: boolean;
    
    // For 'box' variant
    boxLabel?: string;
    boxSquareLabel?: string;
    boxPlaceholderTitle?: string;
    boxPlaceholderDesc?: string;
    
    // For 'avatar' variant
    avatarClassName?: string;
    fallbackClassName?: string;
    fallbackInitial?: string;
    secondaryImageFallback?: string; // e.g., officePhoto
    showProgressOverlay?: boolean;
    badge?: React.ReactNode; // e.g., ShieldCheck icon
}

export const AgencySharedImageField: React.FC<AgencySharedImageFieldProps> = ({
    variant,
    field,
    profileData,
    isImageSubmitting,
    uploadProgress,
    getImageUrl,
    getOriginalImageUrl,
    onFileSelected,
    handleRecrop,
    handleOpenHistory,
    openLightbox,
    language,
    disabled = false,
    readOnly = false,
    boxLabel,
    boxSquareLabel,
    boxPlaceholderTitle,
    boxPlaceholderDesc,
    avatarClassName,
    fallbackClassName,
    fallbackInitial = 'A',
    secondaryImageFallback,
    showProgressOverlay = false,
    badge
}) => {
    const imageUrl = getImageUrl(profileData, field);
    const originalImageUrl = getOriginalImageUrl(profileData, field);
    
    // TypeScript type casting for history arrays which might be any in Partial<AgencyProfile>
    const historyArray = ((profileData as any)[`${field}History`] as string[]) || [];
    
    const inputId = `${field}-upload-input`;

    const [isHovered, setIsHovered] = React.useState(false);
    const imgContainerRef = React.useRef<HTMLDivElement>(null);
    const lensRef = React.useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!imageUrl) return;
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const rawX = ((e.clientX - left) / width) * 100;
        const rawY = ((e.clientY - top) / height) * 100;
        const x = Math.max(0, Math.min(100, rawX));
        const y = Math.max(0, Math.min(100, rawY));
        if (imgContainerRef.current) {
            imgContainerRef.current.style.transformOrigin = `${x}% ${y}%`;
        }
        if (lensRef.current) {
            lensRef.current.style.left = `${x}%`;
            lensRef.current.style.top = `${y}%`;
        }
    };

    const triggerClick = () => {
        if (imageUrl) {
            openLightbox([originalImageUrl || imageUrl]);
        } else {
            document.getElementById(inputId)?.click();
        }
    };

    const renderDropdownItems = () => {
        if (!imageUrl) {
            return (
                <DropdownMenuItem 
                    className="rounded-lg gap-2 cursor-pointer py-2.5"
                    onClick={() => document.getElementById(inputId)?.click()}
                >
                    <Upload className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-700">{agencyTranslations.imageActions.change[language]}</span>
                </DropdownMenuItem>
            );
        }

        return (
            <>
                <DropdownMenuItem 
                    className="rounded-lg gap-2 cursor-pointer py-2.5"
                    onClick={() => {
                        if (originalImageUrl) openLightbox([originalImageUrl]);
                    }}
                >
                    <Search className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-700">{agencyTranslations.imageActions.view[language]}</span>
                </DropdownMenuItem>
                
                {originalImageUrl && (
                    <DropdownMenuItem 
                        className="rounded-lg gap-2 cursor-pointer py-2.5"
                        onClick={() => handleRecrop(originalImageUrl, field)}
                    >
                        <RotateCw className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{agencyTranslations.imageActions.recrop[language]}</span>
                    </DropdownMenuItem>
                )}
                
                <DropdownMenuItem 
                    className="rounded-lg gap-2 cursor-pointer py-2.5"
                    onClick={() => document.getElementById(inputId)?.click()}
                >
                    <Camera className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-700">{agencyTranslations.imageActions.change[language]}</span>
                </DropdownMenuItem>
                
                {historyArray.length >= 2 && (
                    <DropdownMenuItem 
                        className="rounded-lg gap-2 cursor-pointer py-2.5"
                        onClick={() => handleOpenHistory(field)}
                    >
                        <BookCopy className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{agencyTranslations.imageActions.restore[language]}</span>
                    </DropdownMenuItem>
                )}
            </>
        );
    };

    // UI FOR BOX VARIANT (Luồng Đăng ký Update Profile)
    if (variant === 'box') {
        const defaultIcon = field === 'representativePhoto' ? <User className="w-6 h-6 text-[#B9E6FF]" /> : <Building2 className="w-6 h-6 text-[#B9E6FF]" />;
        
        return (
            <div className="space-y-2 text-[#0D8DC8] md:col-span-2">
                <Label className="font-bold flex items-center justify-between">
                    <span>{boxLabel}</span>
                    <span className="text-[10px] text-slate-400 italic">{boxSquareLabel}</span>
                </Label>
                <div className="flex items-center gap-4 p-4 bg-[#F0F9FF] rounded-xl border-2 border-dashed border-[#B9E6FF] transition-all hover:bg-[#E5F5FF]">
                    <div className="relative group shrink-0">
                    <HoverCard openDelay={100} closeDelay={100}>
                        <HoverCardTrigger asChild>
                            <div 
                                className="w-16 h-16 rounded-lg bg-white border border-[#B9E6FF] flex items-center justify-center overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-all"
                                onClick={triggerClick}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                onMouseMove={handleMouseMove}
                            >
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        className="w-full h-full object-contain p-1.5 transition-transform duration-500 group-hover:scale-110"
                                        alt="Uploaded field"
                                    />
                                ) : (
                                    defaultIcon
                                )}
                                {isImageSubmitting && (
                                    <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] flex items-center justify-center">
                                        <Loader2 className="w-4 h-4 animate-spin text-[#19A6DF]" />
                                    </div>
                                )}
                                
                                {imageUrl && isHovered && (
                                    <div 
                                        ref={lensRef}
                                        className="absolute pointer-events-none border border-primary/40 bg-primary/10 backdrop-blur-[0.5px] shadow-[0_0_0_9999px_rgba(0,0,0,0.15)] rounded-sm z-20"
                                        style={{ 
                                            width: '40%', 
                                            height: '40%', 
                                            transform: 'translate(-50%, -50%)',
                                            left: '50%',
                                            top: '50%',
                                            transition: 'left 0.02s ease-out, top 0.02s ease-out'
                                        }}
                                    />
                                )}
                            </div>
                        </HoverCardTrigger>
                        {imageUrl && (
                            <HoverCardContent 
                                className="p-0 overflow-hidden border-none shadow-2xl rounded-2xl bg-white/95 backdrop-blur-md animate-in zoom-in-95 duration-200 z-50 w-[90vw] sm:w-[500px] md:w-[768px]"
                                side="top"
                                align="center"
                                sideOffset={12}
                            >
                                <div className="relative w-full h-[400px] bg-slate-50 flex items-center justify-center border-b border-slate-100 overflow-hidden">
                                    <div 
                                        className="relative w-full h-full p-2"
                                        ref={imgContainerRef}
                                        style={{ 
                                            transformOrigin: `50% 50%`,
                                            transform: 'scale(2.5)',
                                            transition: 'transform-origin 0.02s ease-out'
                                        }}
                                    >
                                        <img 
                                            src={getOriginalImageUrl(profileData, field) || imageUrl} 
                                            alt={boxLabel || 'Preview'} 
                                            className="w-full h-full object-contain" 
                                        />
                                    </div>
                                    <div className="absolute top-2 right-2 pointer-events-none">
                                        <div className="bg-primary/10 backdrop-blur-sm text-primary px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider">
                                            {language === 'vi' ? 'Xem trước' : 'Preview'}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4 bg-white/50">
                                    <h4 className="text-sm font-bold text-slate-800 leading-tight">{boxLabel}</h4>
                                    <p className="text-[10px] text-slate-400 font-medium uppercase tracking-tighter mt-1">
                                        {agencyTranslations.imageActions.view[language]}
                                    </p>
                                </div>
                            </HoverCardContent>
                        )}
                    </HoverCard>

                        {!readOnly && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        disabled={disabled}
                                        className="absolute -bottom-1.5 -right-1.5 bg-[#19A6DF] text-white p-1 rounded-full shadow-lg border-2 border-white cursor-pointer hover:bg-[#0D8DC8] hover:scale-110 transition-all z-10"
                                    >
                                        <Camera className="w-3 h-3" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-48 p-1 rounded-xl shadow-xl border-slate-100">
                                    {renderDropdownItems()}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        <input
                            type="file"
                            id={inputId}
                            className="hidden"
                            accept="image/*"
                            disabled={disabled || readOnly}
                            onChange={(e) => onFileSelected(e, field)}
                        />
                    </div>
                    <div className="flex-grow space-y-1">
                        <p className="text-[11px] font-bold text-blue-700">{boxPlaceholderTitle}</p>
                        {isImageSubmitting && uploadProgress !== undefined ? (
                            <div className="pt-1">
                                <div className="flex justify-between text-[9px] font-black text-[#19A6DF] mb-0.5 uppercase tracking-wider">
                                    <span>Đang tải...</span>
                                    <span>{uploadProgress}%</span>
                                </div>
                                <Progress value={uploadProgress} className="h-1 bg-white" />
                            </div>
                        ) : (
                            <p className="text-[10px] text-blue-400 leading-tight">{boxPlaceholderDesc}</p>
                        )}
                    </div>
                </div>
            </div>
        );
    } // END BOX VARIANT

    // UI FOR AVATAR VARIANT (Profile Display)
    const effectiveSrc = imageUrl || secondaryImageFallback;
    const isCompanyLogo = field === 'companyLogo';
    
    return (
        <div className="relative shrink-0 group/avatar">
            {badge}
            <HoverCard openDelay={100} closeDelay={100}>
                <HoverCardTrigger asChild>
                    <Avatar 
                        className={cn(
                            "border-4 border-white shadow-xl shadow-slate-200/60 bg-white overflow-hidden shrink-0 relative z-10 transition-transform duration-500 cursor-zoom-in", 
                            avatarClassName
                        )}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onMouseMove={handleMouseMove}
                        onClick={() => imageUrl && openLightbox([originalImageUrl || imageUrl])}
                    >
                        <AvatarImage
                            src={effectiveSrc}
                            className={cn(isCompanyLogo ? "object-contain p-2" : "object-cover", "transition-transform duration-500 group-hover/avatar:scale-110")}
                            onError={(e) => {
                                if (secondaryImageFallback && imageUrl) {
                                    (e.target as HTMLImageElement).src = secondaryImageFallback;
                                }
                            }}
                        />
                        <AvatarFallback className={cn("text-3xl font-bold", fallbackClassName)}>
                            {fallbackInitial}
                        </AvatarFallback>
                        
                        {showProgressOverlay && uploadProgress !== undefined && (
                            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center z-40">
                                <Loader2 className="w-6 h-6 animate-spin text-[#F2B92A] mb-1" />
                                <span className="text-[10px] font-black text-[#F2B92A]">{uploadProgress}%</span>
                            </div>
                        )}

                        {imageUrl && isHovered && (
                            <div 
                                ref={lensRef}
                                className="absolute pointer-events-none border border-primary/40 bg-primary/10 backdrop-blur-[0.5px] shadow-[0_0_0_9999px_rgba(0,0,0,0.15)] rounded-full z-20"
                                style={{ 
                                    width: '40%', 
                                    height: '40%', 
                                    transform: 'translate(-50%, -50%)',
                                    left: '50%',
                                    top: '50%',
                                    transition: 'left 0.02s ease-out, top 0.02s ease-out'
                                }}
                            />
                        )}
                    </Avatar>
                </HoverCardTrigger>
                {imageUrl && (
                    <HoverCardContent 
                        className="p-0 overflow-hidden border-none shadow-2xl rounded-2xl bg-white/95 backdrop-blur-md animate-in zoom-in-95 duration-200 z-50 w-[90vw] sm:w-[500px] md:w-[768px]"
                        side={isCompanyLogo ? "right" : "top"}
                        align="center"
                        sideOffset={12}
                    >
                        <div className="relative w-full h-[400px] bg-slate-50 flex items-center justify-center border-b border-slate-100 overflow-hidden">
                            <div 
                                className="relative w-full h-full p-2"
                                ref={imgContainerRef}
                                style={{ 
                                    transformOrigin: `50% 50%`,
                                    transform: 'scale(2.5)',
                                    transition: 'transform-origin 0.02s ease-out'
                                }}
                            >
                                <img 
                                    src={getOriginalImageUrl(profileData, field) || imageUrl} 
                                    alt={isCompanyLogo ? 'Company Logo' : 'Representative Photo'} 
                                    className="w-full h-full object-contain" 
                                />
                            </div>
                            <div className="absolute top-2 right-2 pointer-events-none">
                                <div className="bg-primary/10 backdrop-blur-sm text-primary px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider">
                                    {language === 'vi' ? 'Xem trước' : 'Preview'}
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-white/50">
                            <h4 className="text-sm font-bold text-slate-800 leading-tight">
                                {isCompanyLogo ? (profileData.companyName || 'Logo') : (profileData.managingDirector || 'MD Photo')}
                            </h4>
                        </div>
                    </HoverCardContent>
                )}
            </HoverCard>
            
            {!readOnly && (
                <div className={cn("absolute z-30", isCompanyLogo ? "-bottom-1 -right-1" : "top-0 right-0")} style={{ transform: isCompanyLogo ? 'none' : 'translate(0.5rem, -0.5rem)' }}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                disabled={disabled}
                                className={cn(
                                    "rounded-full text-white flex items-center justify-center shadow-lg border-2 border-white transition-colors active:scale-95",
                                    isCompanyLogo ? "w-9 h-9 bg-[#19A6DF] hover:bg-[#0D8DC8]" : "w-8 h-8 bg-[#F2B92A] hover:bg-[#d9a42a]"
                                )}
                                title={agencyTranslations.imageActions.change[language]}
                            >
                                {uploadProgress !== undefined
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <Camera className="w-4 h-4" />
                                }
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align={isCompanyLogo ? "end" : "start"} side={isCompanyLogo ? "bottom" : "right"} className="w-52 p-1 rounded-xl shadow-xl border-slate-100">
                            {renderDropdownItems()}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            )}
            
            {isCompanyLogo && uploadProgress !== undefined && (
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-32 bg-white/95 backdrop-blur-md px-2 pt-1.5 pb-2 rounded-xl shadow-xl z-30">
                    <div className="flex justify-between text-[9px] font-bold text-[#19A6DF] mb-1">
                        <span>{agencyTranslations.imageActions.loading[language]}</span>
                        <span>{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-1 bg-blue-100" />
                </div>
            )}
            
             <input
                type="file"
                id={inputId}
                className="hidden"
                accept="image/*"
                disabled={disabled || readOnly}
                onChange={(e) => onFileSelected(e, field)}
            />
        </div>
    );
};
